/**
 * TerminatedUserGuard Component (V72)
 *
 * 퇴사 처리된 사용자의 ERP 접근을 차단합니다.
 *
 * **정책:**
 * - 퇴사일(leaveDate) = 마지막 근무일 (당일까지 사용 가능)
 * - 퇴사일 다음날부터 접근 차단
 * - status === 'terminated' 이거나 leaveDate < today(KST) 인 경우 차단
 *
 * **플로우:**
 * 1. 사용자 프로필 로드
 * 2. 퇴사 여부 판단
 * 3. 퇴사 처리된 경우 TerminatedPage 표시 + 자동 로그아웃
 * 4. 정상인 경우 children 렌더링
 */

import React from 'react';
import { Loader } from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { TerminatedPage } from '../../pages/auth/TerminatedPage';

interface TerminatedUserGuardProps {
  children: React.ReactNode;
}

/**
 * KST 기준 오늘 날짜 문자열 반환 (YYYY-MM-DD)
 *
 * 백엔드의 dateUtils.ts와 동일한 로직
 */
function getKSTTodayString(): string {
  const now = new Date();
  // KST = UTC + 9시간
  const kstOffset = 9 * 60 * 60 * 1000;
  const kstDate = new Date(now.getTime() + kstOffset);
  return kstDate.toISOString().split('T')[0];
}

/**
 * 주어진 날짜가 오늘(KST) 이전인지 확인
 *
 * @param dateStr YYYY-MM-DD 형식의 날짜 문자열
 * @returns 해당 날짜가 오늘 이전이면 true (오늘이면 false)
 */
function isBeforeTodayKST(dateStr: string | null | undefined): boolean {
  if (!dateStr) {
    return false;
  }
  const today = getKSTTodayString();
  return dateStr < today;
}

/**
 * 사용자 퇴사 여부 확인
 *
 * @param status 사용자 상태
 * @param leaveDate 퇴사일 (YYYY-MM-DD)
 * @returns 퇴사 처리된 경우 true
 */
function isUserTerminated(
  status: string | undefined,
  leaveDate: string | undefined
): boolean {
  // 이미 terminated 상태인 경우
  if (status === 'terminated') {
    return true;
  }

  // 퇴사일이 오늘 이전인 경우 (스케줄러가 아직 실행 안 된 케이스)
  if (leaveDate && isBeforeTodayKST(leaveDate)) {
    return true;
  }

  return false;
}

export function TerminatedUserGuard({ children }: TerminatedUserGuardProps) {
  const { user, isLoading } = useCurrentUser();

  // 로딩 중
  if (isLoading) {
    return (
      <div style={loadingContainerStyle}>
        <Loader
          size={40}
          style={{
            color: '#3b82f6',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ marginTop: '12px', color: '#6b7280' }}>로딩 중...</p>
      </div>
    );
  }

  // 사용자 정보가 없는 경우 (OnboardingGuard에서 처리)
  if (!user?.profile) {
    return <>{children}</>;
  }

  const profile = user.profile;

  // V72: 퇴사 여부 확인
  const terminated = isUserTerminated(profile.status, profile.leaveDate);

  if (terminated) {
    console.warn(
      `[V72] 퇴사 처리된 사용자 접근 차단: ` +
      `uid=${profile.id}, status=${profile.status}, leaveDate=${profile.leaveDate}`
    );

    return (
      <TerminatedPage
        leaveDate={profile.leaveDate}
        displayName={profile.displayName}
      />
    );
  }

  // 정상 사용자
  return <>{children}</>;
}

const loadingContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: '#f9fafb',
};
